/**
 * Offline queue for lead saves AND opaque-token scans.
 *
 * Two item shapes share one queue:
 *
 *   1. `kind: 'lead'` — the original flow. When `api.leads.create` fails
 *      because of network/connectivity issues, the payload (with its already-
 *      minted `clientId`) is persisted here. Server-side dedupe by `clientId`
 *      makes every retry safe.
 *
 *   2. `kind: 'token'` — Phase 5.36.7 opaque-token offline path. When the
 *      booth scans an `sd1:tok_…` badge but `/scan/resolve` fails with a
 *      transient/offline error, we stash the token here. On sync, the queue
 *      flusher resolves the token first, then writes a lead — same shape the
 *      online path would have produced. If resolve fails permanently
 *      (404 / 410 / wrong_event / qr_mode_not_opaque / token_revoked /
 *      no_active_event), the item is dropped with a note in `lastError` so
 *      the operator can see it in the "needs review" strip on the pending
 *      pill.
 *
 * Backwards compatibility:
 *   - Items written by older clients (pre-5.36.7) have no `kind` field.
 *     The reader fills in `kind: 'lead'` so existing queued leads keep
 *     flushing without a migration step.
 *
 * Storage shape (key bumped to v2 to make the kind transition explicit,
 * with a one-time read-side migration from v1):
 *   localStorage['scandeck.queue.v2'] = JSON.stringify(QueuedItem[])
 */

import type { NewLead } from './types';
import { accessScope, subscribeAccess } from './accessScope';

// Unattributed v1/v2 queues are deliberately left untouched, never replayed
// into whichever teammate happens to sign in next.
function storageKey(): string | null {
	const scope = accessScope();
	return scope ? `scandeck.queue.v3:${scope}` : null;
}

/** Lead-save queue item (the original shape). */
export interface QueuedLeadItem {
	kind: 'lead';
	/** Stable id used both for retry de-dup on the server AND React keys. */
	clientId: string;
	/** Lead payload sans vendorId (vendorId is derived from auth token server-side). */
	payload: Omit<NewLead, 'vendorId'>;
	/** ms epoch — when the user pressed Save. */
	queuedAt: number;
	/** How many retry attempts we've made (capped, but used only for display). */
	attempts: number;
	/** Last error message (if any), for diagnostics. */
	lastError?: string;
}

/**
 * Opaque-token queue item. We don't have the attendee details yet — they live
 * server-side behind `/scan/resolve`. On flush we resolve, then write the lead.
 */
export interface QueuedTokenItem {
	kind: 'token';
	/** Stable id used both for the eventual lead's clientId AND React keys. */
	clientId: string;
	/** Raw `tok_…` (the `sd1:` wire prefix is stripped before enqueue). */
	token: string;
	/** Full raw scan including prefix — kept so we can populate `lead.raw`. */
	rawScan: string;
	/** ms epoch — when the operator scanned the badge. */
	queuedAt: number;
	/** How many resolve attempts we've made. */
	attempts: number;
	/** Last error message / reason, for diagnostics. */
	lastError?: string;
	/**
	 * Issue 892: notes and tags the operator added while the badge was still
	 * waiting for `/scan/resolve`. Merged into the lead payload at promotion
	 * so an offline capture carries the same annotations as an online one.
	 */
	notes?: string | null;
	tags?: string[];
}

export type QueuedItem = QueuedLeadItem | QueuedTokenItem;

/** Notes and tags an operator can attach to a pending badge. */
export interface TokenAnnotations {
	notes: string | null;
	tags: string[];
}

type Listener = (items: QueuedItem[]) => void;

const listeners = new Set<Listener>();

/**
 * Coerce a parsed item to a typed QueuedItem. Items written by older clients
 * (no `kind` field) are treated as `kind: 'lead'` so we don't lose pending
 * leads across a deploy.
 */
function coerce(x: unknown): QueuedItem | null {
	if (!x || typeof x !== 'object') return null;
	const o = x as Record<string, unknown>;
	if (typeof o.clientId !== 'string') return null;
	if (typeof o.queuedAt !== 'number' || typeof o.attempts !== 'number') return null;

	const kind = o.kind === 'token' ? 'token' : 'lead';
	if (kind === 'token') {
		if (typeof o.token !== 'string' || typeof o.rawScan !== 'string') return null;
		return {
			kind: 'token',
			clientId: o.clientId,
			token: o.token,
			rawScan: o.rawScan,
			queuedAt: o.queuedAt,
			attempts: o.attempts,
			lastError: typeof o.lastError === 'string' ? o.lastError : undefined,
			notes: typeof o.notes === 'string' ? o.notes : null,
			tags: Array.isArray(o.tags) ? o.tags.filter((t): t is string => typeof t === 'string') : [],
		};
	}

	if (!o.payload || typeof o.payload !== 'object') return null;
	return {
		kind: 'lead',
		clientId: o.clientId,
		payload: o.payload as QueuedLeadItem['payload'],
		queuedAt: o.queuedAt,
		attempts: o.attempts,
		lastError: typeof o.lastError === 'string' ? o.lastError : undefined,
	};
}

function read(): QueuedItem[] {
	try {
		const key = storageKey();
		if (!key) return [];
		const raw = localStorage.getItem(key);
		if (!raw) return [];
		const parsed = JSON.parse(raw);
		if (!Array.isArray(parsed)) return [];
		const items: QueuedItem[] = [];
		for (const x of parsed) {
			const c = coerce(x);
			if (c) items.push(c);
		}
		return items;
	} catch {
		return [];
	}
}

function write(items: QueuedItem[]): void {
	const key = storageKey();
	if (!key) throw new Error('Sign in before saving pending scans');
	try {
		localStorage.setItem(key, JSON.stringify(items));
	} catch (err) {
		throw new Error('Device storage is unavailable. This scan has not been saved.');
	}
	for (const fn of listeners) fn(items);
}

subscribeAccess(() => { for (const listener of listeners) listener(read()); });

export const offlineQueue = {
	hasUnattributedPending(): boolean {
		try {
			const keys = ['scandeck.queue.v1', 'scandeck.queue.v2'];
			// Preserve other identity/policy queues, but make their existence visible.
			// Never reassign their contents to the current authenticated teammate.
			for (let i = 0; i < localStorage.length; i++) {
				const key = localStorage.key(i);
				if (key?.startsWith('scandeck.queue.v3:') && key !== storageKey()) keys.push(key);
			}
			return keys.some(key => {
				const items = JSON.parse(localStorage.getItem(key) ?? '[]');
				return Array.isArray(items) && items.length > 0;
			});
		} catch { return false; }
	},
	/** Read-only snapshot of pending items. */
	list(): QueuedItem[] {
		return read();
	},

	/** True if there's at least one pending item. */
	hasPending(): boolean {
		return read().length > 0;
	},

	/**
	 * Push a lead payload onto the tail of the queue.
	 *
	 * If an item with this clientId is already queued (lead OR token), we
	 * replace it in place — the user could re-submit the same draft, or the
	 * online resolve path could promote a token item into a lead item using
	 * the original clientId.
	 */
	enqueue(item: Omit<QueuedLeadItem, 'kind' | 'queuedAt' | 'attempts'>): void {
		const items = read();
		const existing = items.findIndex((x) => x.clientId === item.clientId);
		const next: QueuedLeadItem = {
			kind: 'lead',
			...item,
			queuedAt: Date.now(),
			attempts: 0,
		};
		if (existing >= 0) {
			items[existing] = next;
		} else {
			items.push(next);
		}
		write(items);
	},

	/**
	 * Phase 5.36.7 — push an opaque token onto the queue when `/scan/resolve`
	 * is unreachable. Same dedupe rule as `enqueue()`: replace any existing
	 * item with the same clientId so re-scans of the same badge don't pile up.
	 */
	/** True when an item with this clientId is already waiting in the queue. */
	hasClientId(clientId: string): boolean {
		return read().some((x) => x.clientId === clientId);
	},

	enqueueToken(item: Omit<QueuedTokenItem, 'kind' | 'queuedAt' | 'attempts'>): void {
		const items = read();
		const existing = items.findIndex((x) => x.clientId === item.clientId);
		const prior = existing >= 0 ? items[existing] : null;
		const next: QueuedTokenItem = {
			kind: 'token',
			...item,
			queuedAt: Date.now(),
			attempts: 0,
			// A re-scan of a badge that is already pending must not wipe the
			// notes and tags the operator typed after the first scan.
			notes: item.notes ?? (prior?.kind === 'token' ? prior.notes ?? null : null),
			tags: item.tags ?? (prior?.kind === 'token' ? prior.tags ?? [] : []),
		};
		if (existing >= 0) {
			items[existing] = next;
		} else {
			items.push(next);
		}
		write(items);
	},

	/** The pending token item for a badge, if it is still waiting to resolve. */
	getToken(clientId: string): QueuedTokenItem | null {
		const found = read().find((x) => x.clientId === clientId);
		return found && found.kind === 'token' ? found : null;
	},

	/**
	 * Issue 892: attach notes and tags to a pending badge. No-op when the
	 * item has already been promoted or flushed; the caller falls back to the
	 * normal lead edit path in that case.
	 */
	annotateToken(clientId: string, annotations: TokenAnnotations): boolean {
		const items = read();
		const idx = items.findIndex((x) => x.clientId === clientId);
		const item = items[idx];
		if (!item || item.kind !== 'token') return false;
		items[idx] = { ...item, notes: annotations.notes, tags: annotations.tags };
		write(items);
		return true;
	},

	/** Remove an item by clientId (called after a successful flush). */
	remove(clientId: string): void {
		write(read().filter((x) => x.clientId !== clientId));
	},

	/**
	 * Replace a token item with a lead item, preserving the original clientId
	 * and queuedAt. Used by the flusher when `/scan/resolve` succeeds but the
	 * subsequent lead-create fails transiently — we don't want to resolve the
	 * token a second time on the next retry (the resolve audits + rate-limit
	 * counter on the worker side would double-count), and we don't want to
	 * lose the work.
	 */
	promoteTokenToLead(
		clientId: string,
		payload: Omit<NewLead, 'vendorId'>,
	): void {
		const items = read();
		const idx = items.findIndex((x) => x.clientId === clientId);
		if (idx < 0) return;
		const prior = items[idx];
		const lead: QueuedLeadItem = {
			kind: 'lead',
			clientId,
			payload,
			queuedAt: prior.queuedAt,
			attempts: 0,
		};
		items[idx] = lead;
		write(items);
	},

	/** Bump the attempt counter and stash the last error message. */
	markAttempt(clientId: string, error?: string): void {
		const items = read();
		const idx = items.findIndex((x) => x.clientId === clientId);
		if (idx < 0) return;
		const current = items[idx];
		items[idx] = {
			...current,
			attempts: current.attempts + 1,
			lastError: error,
		} as QueuedItem;
		write(items);
	},

	/** Subscribe to queue changes. Returns an unsubscribe function. */
	subscribe(fn: Listener): () => void {
		listeners.add(fn);
		// Push the current state immediately so subscribers don't have to read separately.
		fn(read());
		return () => {
			listeners.delete(fn);
		};
	},

	/** Drop everything. Used by Settings → "Clear pending queue". */
	clear(): void {
		write([]);
	},
};

/**
 * Decide whether a thrown error should send the payload to the offline queue
 * vs. surfacing it to the user as a hard failure.
 *
 * Treated as transient (queue + retry):
 *   - TypeError ("Failed to fetch", "Load failed", etc.)
 *   - AbortError / timeouts
 *   - Messages mentioning network / offline
 *   - navigator.onLine === false
 *   - ApiError with HTTP 5xx (502/503/504 in particular — Cloudflare returns
 *     these briefly during worker deploys or transient upstream blips).
 *     The server returned a real response, so this isn't a connectivity
 *     failure, but it's the same retry-with-backoff shape: the user shouldn't
 *     have to re-tap, and the queue's existing dedupe + replay handle it.
 *   - WorkerApiError with HTTP 5xx, same logic (the workerApi client uses
 *     a different error class than api.ts but the retry semantics match).
 *
 * NOT transient (surface as hard failure — retrying won't help):
 *   - HTTP 4xx (validation, auth, not-found, rate-limited)
 *   - ApiError / WorkerApiError with a plain RPC message like "Not signed in"
 */
export function isTransientNetworkError(err: unknown): boolean {
	if (!err) return false;
	if (typeof err !== 'object') return false;
	const name = (err as { name?: string }).name;
	const message = String((err as { message?: string }).message || '');
	const status = (err as { status?: number }).status;
	if (typeof status === 'number' && status >= 400 && status < 500 && status !== 408) return false;
	if (name === 'ApiError' && /→ HTTP 4\d{2}\b/.test(message) && !/→ HTTP 408\b/.test(message)) return false;
	if (name === 'TypeError') return true;
	if (name === 'AbortError') return true;
	if (/network|failed to fetch|load failed|timeout|offline/i.test(message)) return true;
	if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
	// HTTP 5xx — either via ApiError's "→ HTTP 5NN" message convention or
	// WorkerApiError's typed `.status` field.
	if (name === 'ApiError' && /→ HTTP 5\d{2}\b/.test(message)) return true;
	if (typeof status === 'number' && status >= 500 && status < 600) return true;
	return false;
}
